import { inject, injectable } from 'inversify'
import _ from 'lodash'
import Debug from 'debug';
import { DualCommandQueue }from '../../shared/dual-command-queue'
import { TrackingService2, TrackingService2Symbol, createCommand, createQuery } from '@/app/tracking/api';
import { QuizSessionStoreModule, QuizSessionStoreModuleSymbol } from '../store'
import { QuizSessionReducerPluginFactorySymbol, QuizSessionReducerPlugin } from '../api';
import { UpdateQuizSessionTrackingCommand, QueryQuizSessionsTrackingQuery,
         DeleteQuizSessionTrackingCommand } from '../tracking/api';
import { cloneRemovingVueObserver, checkVueObserver } from '@/app/shared/utils/vue-observer-utils';
import { CancelToken } from '@/app/shared/utils';

const debug = Debug('exam-result-service')

export const QuizSessionServiceSymbol = Symbol.for('QuizSessionService2')

@injectable()
export class QuizSessionService {

  private queue: DualCommandQueue;

  constructor(
    @inject(QuizSessionStoreModuleSymbol)
    private quizSessionStoreModule: QuizSessionStoreModule,
    @inject(QuizSessionReducerPluginFactorySymbol)
    private reducerFactory: (type: string) => QuizSessionReducerPlugin<unknown, unknown>,
    @inject(TrackingService2Symbol)
    private trackingService: TrackingService2) {

    this.queue = new DualCommandQueue('quiz-session-service')
  }

  startSession<TCommand>(sessionId: string, quizId: string, aspect: string,
                         type: string, initialCommand: TCommand) {
    this.queue.enqueueLocal('start-session', async () => {

      debug('start-session', sessionId, quizId, aspect, type)
      initialCommand = checkVueObserver(initialCommand)
      
      this.quizSessionStoreModule.addNewSession({
        id: sessionId,
        type: type,
        quizId: quizId,
        aspect: aspect,
        initialCommand: initialCommand,
        reducerFactory: this.reducerFactory
      })

      this.updateSessionInTracking(sessionId)
    })
  }

  updateSession<TCommand>(sessionId: string, command: TCommand) {
    this.queue.enqueueLocal('update-session', async () => {

      debug('update-session')

      command = checkVueObserver(command)
      this.quizSessionStoreModule.updateSession({
        id: sessionId,
        command: command,
        reducerFactory: this.reducerFactory
      })
      this.updateSessionInTracking(sessionId)
    })
  }

  private updateSessionInTracking(sessionId: string) {
    const state = this.quizSessionStoreModule.findSession(sessionId)
    if (state === false) { throw new Error('Coding error: missing session from the store: ' + sessionId)}

    const clonedState = cloneRemovingVueObserver(state.state)

    debug('storing session in tracking', sessionId, state.state, clonedState)
    const trackingCommand = createCommand<UpdateQuizSessionTrackingCommand>('update-quiz-session', {
      id: sessionId,
      quizId: state.quizId,
      aspect: state.aspect,
      type: state.type,
      phase: state.phase,
      state: clonedState
    })
    this.trackingService.execute(trackingCommand)
  }

  finishSession(sessionId: string) {
    this.queue.enqueueLocal('finish-session', async () => {
      this.quizSessionStoreModule.finishSession(sessionId)
      this.deleteSessionInTracking(sessionId)
    })
  }

  abortSession(sessionId: string) {
    this.queue.enqueueLocal('abort-session', async () => {
      this.quizSessionStoreModule.abortSession(sessionId)
      this.deleteSessionInTracking(sessionId)
    })
  }

  private deleteSessionInTracking(sessionId: string) {
    debug('deleting session in tracking', sessionId)
    const trackingCommand = createCommand<DeleteQuizSessionTrackingCommand>('delete-quiz-session', {
      id: sessionId
    })
    this.trackingService.execute(trackingCommand)
  }

  public start() {
    debug('starting QuizSessionService');

    this.queue.start();
    this.queue.enqueueRemote('query-tracking-state', (cancel) => this.queryTrackingState(cancel));
  }

  public async stop() {
    debug('stopping QuizSessionService');

    await this.queue.stop()
  }

  private async queryTrackingState(cancel: CancelToken) {
    debug(`querying quiz sessions' tracking state`);

    this.quizSessionStoreModule.startRemoteQuerying()

    const query = createQuery<QueryQuizSessionsTrackingQuery>('query-quiz-sessions', {})
    const result = await this.trackingService.query(query, cancel)

    const cloned = _.cloneDeep(result.sessions)
    debug(`quiz sessions' remote tracking state queried: %j`, cloned);
    this.quizSessionStoreModule.finishRemoteQuerying(cloned)
    
  }
}
